// filepath: apps/web/src/admin/counterparties/edit.tsx
//
// FR-Group-B — CounterpartyEditScreen (ISS-442 UX slim + i18n).
//
// Edit-counterparty form. Carries the OCC `row_version` from
// the detail screen so the backend's UPDATE WHERE row_version = $N
// (INV-7) rejects stale submissions with HTTP 409 — the SPA surfaces
// a `StaleVersionError` on 409 (the design-system primitive).
//
// ISS-442 BA spec (verbatim):
//   (a) Move legal_form and tax_id from the list to the detail and
//       edit views.
//   (b) Translation all locales (RU authoritative, KK falls back
//       to RU when a key is missing, EN must have real English).
//       No hardcoded strings. i18n namespace
//       `directory.counterparties.*`.
//
// ISS-431-uatrzt2 React crash fix: the project opts out of
// `react-router-dom` (FR-UI-CABINET-1 AC-9) and uses its own
// `usePathname()` / `navigate()` primitives. The previous
// `useNavigate` + `useParams` imports threw on every render.
//
// INV-8: submit carries a fresh X-Idempotency-Key per attempt so a
// double-submit is replayed as 200 rather than producing a duplicate
// audit row.
//
// INV-9: no `dangerouslySetInnerHTML`; every value flows through JSX
// text nodes.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Card } from "@/design-system/Card";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { navigate } from "@/features/cabinet/usePathname";
import { ApiError } from "@/definitions/types";
import {
  Counterparty,
  LegalForm,
  LEGAL_FORMS,
  getCounterparty,
  newIdempotencyKey,
  updateCounterparty,
} from "./api";

/**
 * ISS-448 acceptance checks AC-2 (Edit) + AC-3 (validation,
 * including the row_version "record changed" banner with
 * Reload) + AC-4 (unprivileged) + AC-5 (clear_tax_id flag).
 *
 * ISS-448 round 2 (B2, architect note_13946): the classifiers
 * check the TYPED `ApiError` fields (`.status` + `.code`),
 * not string substrings. The pre-round-2 string-substring
 * classifier was unreliable because the real `callApi`
 * throw path produced bare "HTTP 403" / "HTTP 409" messages
 * (no `forbidden` / `not_found` / `tax_id_conflict` /
 * `row_version_mismatch` substring to match). The typed
 * check is robust against any server-side change.
 */
function isPermissionDenied(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof ApiError) {
    return e.status === 403 || e.status === 404;
  }
  return false;
}

function isTaxIdConflict(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof ApiError) {
    return e.status === 409 && e.code === "tax_id_conflict";
  }
  return false;
}

function isRowVersionConflict(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof ApiError) {
    return e.status === 409 && e.code === "row_version_mismatch";
  }
  return false;
}

export interface CounterpartyEditScreenProps {
  /**
   * ISS-379 (Slice 0 tenant-alive): the route id from App.tsx's
   * path-matching dispatch (`/admin/counterparties/:id/edit` ->
   * id segment). Required in production (App.tsx always passes it);
   * empty string triggers the "not found" render path.
   */
  id?: string;
  /** Test override for the load pipeline. */
  loader?: (id: string) => Promise<Counterparty>;
  /**
   * ISS-448: test override for the submit pipeline. Defaults
   * to `updateCounterparty(row.id, input, key)` in production.
   * The signature mirrors the new screen's `submit` prop so
   * the routing test can assert the OCC + clear_tax_id
   * payload without round-tripping through `apiFetch`.
   */
  submit?: (
    id: string,
    input: Parameters<typeof updateCounterparty>[1],
    key: string,
  ) => Promise<void>;
}

export function CounterpartyEditScreen({
  id = "",
  loader,
  submit,
}: CounterpartyEditScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();

  const [row, setRow] = useState<Counterparty | null>(null);
  const [name, setName] = useState<string>("");
  // ISS-442 (a): legal_form + tax_id are now in the form (moved
  // from the list). The default legal_form is "llc"
  // (preserved from the previous v1 form) for backward-compat.
  const [taxId, setTaxId] = useState<string>("");
  // ISS-459 BA correction: the default legal_form is "other"
  // (matches apps/web/src/admin/counterparties/new.tsx). The
  // closed enum is {sole_prop, llc, jsc, plc, other}; "other"
  // has no tax_id requirement so the form's optional tax_id is
  // compatible with the form-submitted default. The pre-ISS-448
  // default "corporation" was rejected (not in the enum), the
  // ISS-448 default "llc" was wrong because llc requires a
  // tax_id (migration 0093) but the form leaves tax_id optional.
  const [legalForm, setLegalForm] = useState<LegalForm>("other");
  // ISS-448 BA spec: contact_person_id is OUT of v1 (needs a
  // person picker; BA deferred to a separate FR). Removed
  // from the edit form. The form still preserves the
  // previously-saved value (it just doesn't surface a UI
  // input) so a round-trip PUT does NOT clear the field
  // inadvertently.
  // (No contactPersonId state — the loader no longer reads
  // it and the onSubmit no longer writes it.)
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  // ISS-448 BA spec: clearing tax_id on edit sends the
  // `clear_tax_id` flag (NOT an empty string). The flag is
  // independent of the input's local state — checking the
  // box sends `clear_tax_id: true` to the API, which the
  // server uses to explicitly null the column.
  const [clearTaxId, setClearTaxId] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // ISS-448 AC-4: the edit path renders <PermissionDenied />
  // when the caller lacks directory.access (the server
  // returns 403, which api.ts callApi prefixes with http_403).
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  // ISS-448 AC-3: duplicate tax_id is rendered as a FIELD
  // error on the tax_id input, not as a generic form-level
  // banner.
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  const [stale, setStale] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setStale(false);
    setPermissionDenied(false);
    setTaxIdError(null);
    try {
      const l = loader ?? ((cid: string) => getCounterparty(cid));
      const cp = await l(id);
      setRow(cp);
      setName(cp.name);
      setTaxId(cp.tax_id ?? "");
      setLegalForm(cp.legal_form);
      setEmail(cp.email ?? "");
      setPhone(cp.phone ?? "");
      setAddress(cp.address ?? "");
      setNotes(cp.notes ?? "");
      // The clear-tax_id flag defaults to false on every load.
      // The user re-checks the box on every edit session that
      // wants to clear the field (BA spec: the flag is
      // per-submit, not persistent).
      setClearTaxId(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!row) return;
      setSubmitting(true);
      setError(null);
      setStale(false);
      setPermissionDenied(false);
      setTaxIdError(null);
      try {
        const key = newIdempotencyKey();
        // ISS-448 BA spec: clearing tax_id on edit sends the
        // `clear_tax_id` flag (NOT an empty string). The
        // server's updateCounterparty handler at
        // internal/directory/counterparty_handlers.go uses
        // the flag to explicitly null the column even when
        // tax_id is set to a non-empty value (the BA
        // spec's "the flag is independent of the input
        // value" requirement).
        const taxIdValue = clearTaxId ? null : taxId.trim() || null;
        // ISS-448 round 2 (B3, architect note_13946): the
        // server treats `null` as "leave unchanged" for
        // email / phone / address / notes (per
        // `internal/directory/counterparty_pg_store.go:141-153`
        // — the SET clause is only emitted when
        // `*fields.Email != nil`). The pre-round-2 form sent
        // `null` for cleared fields, which silently left the
        // previous value in place. The fix below sends an
        // empty string `""` for cleared fields (the server
        // decodes `&""` as a non-nil pointer to an empty
        // string → SET email = ''). The tax_id field uses
        // the explicit `clear_tax_id` flag (the only field
        // the BA spec mandates the clear pattern for; the
        // other fields are cleared by sending an empty
        // value).
        const emailValue = email.trim();
        const phoneValue = phone.trim();
        const addressValue = address.trim();
        const notesValue = notes.trim();
        const input = {
          name: name.trim(),
          tax_id: taxIdValue,
          clear_tax_id: clearTaxId,
          legal_form: legalForm,
          email: emailValue,
          phone: phoneValue,
          address: addressValue,
          notes: notesValue,
          row_version: row.row_version,
        };
        const submitFn = submit ?? updateCounterparty;
        await submitFn(row.id, input, key);
        navigate(`/admin/counterparties/${encodeURIComponent(row.id)}`);
      } catch (e2) {
        // ISS-448 AC-3: route the 4 error classes to the right
        // surface. (a) 403 / 404 -> <PermissionDenied />
        // (unprivileged). (b) 409 row_version_mismatch ->
        // <StaleVersionError /> banner with Reload. (c) 409
        // tax_id_conflict -> field-level tax_id error. (d)
        // anything else -> generic form-level banner.
        // ISS-448 round 2 (B2): the classifiers check the
        // TYPED `ApiError` fields (`.status` + `.code`),
        // not string substrings.
        if (isPermissionDenied(e2)) {
          setPermissionDenied(true);
        } else if (isTaxIdConflict(e2)) {
          setTaxIdError(
            t(
              "directory.counterparties.edit.tax_id_conflict",
              "A counterparty with this tax id already exists in this tenant",
            ),
          );
        } else if (isRowVersionConflict(e2)) {
          setStale(true);
        } else {
          setError(e2 instanceof Error ? e2.message : String(e2));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      row,
      name,
      taxId,
      clearTaxId,
      legalForm,
      email,
      phone,
      address,
      notes,
      submit,
    ],
  );

  if (loading) {
    return (
      <p data-testid="counterparty-edit-loading">
        {t("directory.counterparties.edit.loading", "Loading counterparty…")}
      </p>
    );
  }
  if (permissionDenied) {
    // ISS-448 AC-4: unprivileged viewer sees the shared
    // <PermissionDenied /> page (the same component the list
    // screen uses — see list.tsx:199). The component itself
    // is role="alert" so screen readers announce it.
    return (
      <div data-testid="counterparty-edit-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="counterparty-edit-error">
        {t("directory.counterparties.edit.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="counterparty-edit-not-found">
        {t("directory.counterparties.edit.not_found", "Counterparty not found")}
      </p>
    );
  }

  return (
    <section data-testid="counterparty-edit-screen">
      <header>
        <h1 id="counterparty-edit-title">
          {t("directory.counterparties.edit.title", "Edit counterparty")}
        </h1>
      </header>
      {stale ? (
        <StaleVersionError
          entityName={row.name}
          onReload={() => void load()}
          data-testid="counterparty-edit-stale"
        />
      ) : null}
      {error ? (
        <Banner tone="danger" data-testid="counterparty-edit-error">
          {error}
        </Banner>
      ) : null}
      <Card>
        <form onSubmit={onSubmit} aria-labelledby="counterparty-edit-title">
          <div>
            <label htmlFor="counterparty-edit-name">
              {t("directory.counterparties.detail.field.name", "Name")}
            </label>
            <input
              id="counterparty-edit-name"
              required
              minLength={1}
              // ISS-448 BA spec: name up to 512 chars.
              maxLength={512}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="counterparty-edit-name"
            />
          </div>
          {/* ISS-442 (a): tax_id + legal_form now in the edit form
              (moved from list). Localized labels per the new
              i18n namespace.
              ISS-448 BA spec: tax_id up to 64 chars + unique per
              tenant. Field-level error on 409 tax_id_conflict.
              The `clear_tax_id` checkbox sends the explicit
              clear flag (independent of the input's local
              value). */}
          <div>
            <label htmlFor="counterparty-edit-tax-id">
              {t("directory.counterparties.detail.field.tax_id", "Tax id")}
            </label>
            <input
              id="counterparty-edit-tax-id"
              maxLength={64}
              value={taxId}
              onChange={(e) => {
                setTaxId(e.target.value);
                if (taxIdError) setTaxIdError(null);
              }}
              aria-invalid={taxIdError ? true : undefined}
              aria-describedby={
                taxIdError ? "counterparty-edit-tax-id-error" : undefined
              }
              data-testid="counterparty-edit-tax-id"
            />
            {taxIdError ? (
              <p
                role="alert"
                id="counterparty-edit-tax-id-error"
                data-testid="counterparty-edit-tax-id-error"
                className="counterparty-edit-field-error"
              >
                {taxIdError}
              </p>
            ) : null}
            <label
              htmlFor="counterparty-edit-clear-tax-id"
              className="counterparty-edit-clear-tax-id-label"
            >
              <input
                id="counterparty-edit-clear-tax-id"
                type="checkbox"
                checked={clearTaxId}
                onChange={(e) => setClearTaxId(e.target.checked)}
                data-testid="counterparty-edit-clear-tax-id"
              />
              {t("directory.counterparties.edit.clear_tax_id", "Clear tax id")}
            </label>
          </div>
          <div>
            <label htmlFor="counterparty-edit-legal-form">
              {t(
                "directory.counterparties.detail.field.legal_form",
                "Legal form",
              )}
            </label>
            <select
              id="counterparty-edit-legal-form"
              value={legalForm}
              onChange={(e) => setLegalForm(e.target.value as LegalForm)}
              data-testid="counterparty-edit-legal-form"
            >
              {LEGAL_FORMS.map((lf) => (
                <option key={lf} value={lf}>
                  {t(`directory.counterparties.legal_form.${lf}`, lf)}
                </option>
              ))}
            </select>
          </div>
          {/* ISS-448 BA spec: contact_person_id is OUT of v1
              (needs a person picker; BA deferred to a separate
              FR). The field is removed from the edit form. */}
          <div>
            <label htmlFor="counterparty-edit-email">
              {t("directory.counterparties.detail.field.email", "Email")}
            </label>
            <input
              id="counterparty-edit-email"
              type="email"
              // ISS-448 BA spec: email up to 320 chars.
              maxLength={320}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="counterparty-edit-email"
            />
          </div>
          <div>
            <label htmlFor="counterparty-edit-phone">
              {t("directory.counterparties.detail.field.phone", "Phone")}
            </label>
            <input
              id="counterparty-edit-phone"
              type="tel"
              // ISS-448 BA spec: phone up to 64 chars.
              maxLength={64}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="counterparty-edit-phone"
            />
          </div>
          <div>
            <label htmlFor="counterparty-edit-address">
              {t("directory.counterparties.detail.field.address", "Address")}
            </label>
            <input
              id="counterparty-edit-address"
              type="text"
              maxLength={500}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="counterparty-edit-address"
            />
          </div>
          <div>
            <label htmlFor="counterparty-edit-notes">
              {t("directory.counterparties.detail.field.notes", "Notes")}
            </label>
            <textarea
              id="counterparty-edit-notes"
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="counterparty-edit-notes"
            />
          </div>
          <p data-testid="counterparty-edit-row-version">
            {t(
              "directory.counterparties.edit.row_version",
              "Row version (OCC): {{value}}",
              { value: row.row_version },
            )}
          </p>
          <div className="counterparty-edit-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || name.trim().length === 0}
              data-testid="counterparty-edit-submit"
            >
              {submitting
                ? t("directory.counterparties.edit.submitting", "Saving…")
                : t("directory.counterparties.edit.submit", "Save")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                navigate(`/admin/counterparties/${encodeURIComponent(row.id)}`)
              }
              data-testid="counterparty-edit-cancel"
            >
              {t("directory.counterparties.edit.cancel", "Cancel")}
            </Button>
          </div>
        </form>
      </Card>
    </section>
  );
}

export default CounterpartyEditScreen;
